import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import ProjectInput from "@/components/fields/ProjectInput";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/libs/utils";
import { Label } from "@radix-ui/react-label";
import { Loader, Send, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const EmailDialog = ({
  link,
  isCoworker = false,
}: {
  link: string;
  isCoworker?: boolean;
}) => {
  const [emailInput, setEmailInput] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [idProject, setIdProject] = useState<number | null>(null);
  const [isRestricted, setIsRestricted] = useState("restricted");
  const [esgWriteAccess, setEsgWriteAccess] = useState(false);
  const hidePostActions = useHidePostActions();
  const [droit, setDroit] = useState("unrestricted");
  const [pendingExistingShare, setPendingExistingShare] = useState<{
    emails: string[];
    message: string;
  } | null>(null);

  useEffect(() => {
    setIsDisabled(emailInput.trim() === "");
  }, [emailInput]);
  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setEsgWriteAccess(false);
    }
  };

  const onSubmit = async (
    submittedEmails: string[],
    confirmExistingUser = false,
  ) => {
    setIsLoading(true);
    const urlApi = `${import.meta.env.VITE_IP_API}/api/carrier/share-link`;
    const token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

    let response: Response;
    let result: any = null;
    try {
      response = await fetch(urlApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          emails: submittedEmails,
          link: link,
          idProject: idProject,
          accessType: isRestricted,
          esgWriteAccess:
            !isCoworker &&
            isRestricted === "restricted" &&
            esgWriteAccess === true,
          isCoworker: isCoworker,
          droit: isCoworker ? droit : null,
          confirmExistingUser,
        }),
      });
      result = await response.json().catch(() => null);
    } catch {
      setIsLoading(false);
      toast({
        title: "Erreur",
        description: t("Une erreur est survenue"),
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    if (response.status === 409 && result?.requiresConfirmation) {
      setPendingExistingShare({
        emails: submittedEmails,
        message:
          result.message ||
          "Une ou plusieurs adresses email existent déjà avec un autre rôle. Voulez-vous rattacher ces comptes comme intervenants ?",
      });
      setIsLoading(false);
      return;
    }

    if (response.ok && result?.success) {
      setIsLoading(false);
      setOpen(false);
      setPendingExistingShare(null);
      setEmailInput("");
      setErrors([]);
      setEsgWriteAccess(false);
      onOpenChange(false);

      toast({
        title: "Success",
        description: t("Lien partagé avec succès"),
        duration: 4000,
      });
      return;
    }

    setIsLoading(false);
    toast({
      title: "Erreur",
      description: result?.message || t("Une erreur est survenue"),
      variant: "destructive",
      duration: 4000,
    });
  };

  const { t } = useTranslation("dashboard", {
    keyPrefix: "soustraitant",
  });

  const validateEmails = (input: string): string[] => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return input
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .filter((email) => !emailPattern.test(email));
  };

  const handleSubmit = () => {
    const invalidEmails = validateEmails(emailInput);
    if (invalidEmails.length > 0) {
      setErrors(invalidEmails);
      toast({
        title: "Erreur",
        description: `${"L'adresse e-mail n'est pas valide"} : ${invalidEmails.join(", ")}`,
        variant: "destructive",
        duration: 4000,
      });
      return;
    }
    const validEmails = emailInput
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
    onSubmit(validEmails);
  };

  if (hidePostActions) return null;
  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className={buttonVariants({ variant: "default" })}>
          <Share2 className="w-4 h-4 mr-2" />
          {t("Partager le lien")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "sm:max-w-[500px]",
          pendingExistingShare && "pointer-events-none opacity-40"
        )}
      >
        <DialogHeader>
          <DialogTitle>{t("Ajouter des adresses e-mail")}</DialogTitle>
          <DialogDescription asChild className="text-[#595959] mt-4">
            <div className="mt-4 py-2">
              <p>
                {t(
                  "Veuillez saisir la liste des adresses e-mail à qui envoyer le lien d'inscription",
                )}{" "}
                <br />
                {t("séparer chaque adresse par une virgule")}.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Label htmlFor="email" className={"font-semibold mb-2"}>
            Emails
          </Label>
          <Input
            placeholder="ex: email1@test.com, email2@test.com"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          {errors.length > 0 && (
            <div className="text-sm text-red-500">
              {t("Adresses invalides")} {errors.join(", ")}
            </div>
          )}
          <br />
          <br />
          <p className="mt-4 text-[#595959]">
            {" "}
            {t("Veuillez choisir un type d'accès")}{" "}
          </p>
          <Select
            value={isRestricted}
            onValueChange={(value) => {
              setIsRestricted(value);
              if (value !== "restricted") {
                setEsgWriteAccess(false);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choisir un type d'accès" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="restricted">
                  {t("Accès restreint")}
                </SelectItem>
                <SelectItem value="unrestricted">
                  {t("Accès complet")}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {!isCoworker && isRestricted === "restricted" && (
            <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
                checked={esgWriteAccess}
                onChange={(event) => setEsgWriteAccess(event.target.checked)}
              />
              <span>
                Autoriser l ecriture de la partie ESG. L acces restera restreint sur le reste du projet.
              </span>
            </label>
          )}
          <br />
          <p className="mt-4 text-[#595959]">
            {" "}
            {t(
              "Vous pouvez sélectionner un projet à assigner aux adresses, en le choisissant dans la liste ci-dessous",
            )}{" "}
            :{" "}
          </p>
          <ProjectInput
            idFinancer={null}
            disableProject={false}
            isFromIntervenant={true}
            handleChange={(value) => {
              setIdProject(value?.id ?? null);
            }}
          />
          {isCoworker && (
            <>
              <br></br>
              <Label htmlFor="permission" className={"font-semibold mb-2"}>
                Type de permission
              </Label>
              <Select
                value={droit}
                onValueChange={(value) => {
                  setDroit(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={"Choisir un type de permission"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="restricted">Lecture seule</SelectItem>
                    <SelectItem value="unrestricted">
                      Lecture / Écriture
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={isLoading || isDisabled}>
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                {t("Envoi en cours")}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {t("Send")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog
      open={!!pendingExistingShare}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isLoading) {
          setPendingExistingShare(null);
        }
      }}
    >
      <DialogContent
        className="z-[10001] sm:max-w-md"
        overlayClassName="z-[10000] bg-black/60"
      >
        <DialogHeader>
          <DialogTitle>Compte existant</DialogTitle>
          <DialogDescription>
            {pendingExistingShare?.message}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={() => setPendingExistingShare(null)}
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            onClick={() => {
              if (pendingExistingShare) {
                onSubmit(pendingExistingShare.emails, true);
              }
            }}
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Confirmation...
              </>
            ) : (
              "Confirmer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EmailDialog;
